
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { generateTimeSlots, parseStoredDateTime } from "@/utils/timeUtils";

export const useCalendarBookings = (serviceId: string | undefined, selectedDate: Date) => {
  const { toast } = useToast();
  const [bookings, setBookings] = useState<any[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const allTimeSlots = generateTimeSlots();

  const fetchBookings = async () => {
    try {
      if (!serviceId) return;
      
      const svcId = parseInt(serviceId);
      // Use local date formatting to avoid timezone conversion
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const targetDate = `${year}-${month}-${day}`;

      // Get all bookings for the service (including cancelled/declined)
      // This is used for the bookings list view
      const { data: allBookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          services:service_id (
            name,
            price,
            region,
            regions:region (
              currency
            )
          )
        `)
        .eq('service_id', svcId)
        .order('start_time', { ascending: false });

      if (bookingsError) throw bookingsError;

      // For calendar view, only get active bookings (not cancelled/declined)
      // This frees up the calendar slots
      const { data: dayBookings, error: dayBookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          services:service_id (
            name,
            price,
            region,
            regions:region (
              currency
            )
          )
        `)
        .eq('service_id', svcId)
        .eq('booking_date', targetDate)
        .not('status', 'in', '("rejected","cancelled")');

      if (dayBookingsError) throw dayBookingsError;

      const blockedSlots: string[] = [];
      const bookedSlots = new Set<string>();

      dayBookings?.forEach(booking => {
        const startTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
        const endTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
        
        let currentTime = new Date(startTime);
        while (currentTime < endTime) {
          const timeSlot = format(currentTime, 'HH:mm');
          if (booking.status === 'blocked' || booking.status === 'travel') {
            blockedSlots.push(timeSlot);
          } else {
            bookedSlots.add(timeSlot);
          }
          currentTime.setMinutes(currentTime.getMinutes() + 30);
        }
      });

      // Transform bookings to include needed service info
      const transformedBookings = allBookings?.map(booking => {
        const serviceData = booking.services || {};
        const regionData = serviceData.regions || {};
        
        return {
          ...booking,
          service_name: serviceData.name,
          service_price: serviceData.price,
          currency: regionData.currency || 'Ksh' // Provide a default currency if missing
        };
      }) || [];

      setBlockedTimes(blockedSlots);
      setBookings(transformedBookings);
      
      const available = allTimeSlots.filter(slot => 
        !bookedSlots.has(slot) && !blockedSlots.includes(slot)
      );
      setAvailableTimeSlots(available);

    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load bookings",
      });
    }
  };

  useEffect(() => {
    if (serviceId) {
      fetchBookings();
    }
  }, [selectedDate, serviceId]);

  return {
    bookings,
    blockedTimes,
    availableTimeSlots,
    fetchBookings
  };
};
